if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.submitButton.disabled ||
          this.variantIdInput.disabled || !this.variantIdInput.value || this.closest('product-info')?.variantRequestPending) return;

        this.clearNetworkError();
        this.handleErrorMessage();

        if (!this.validateGiftChoices()) return;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        const previousCartCount = this.getDrawerItemCount();

        return fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const body = await response.json();
            // A failed POST can already have reached Shopify. Only its known
            // validation responses are safe to treat as a definite rejection.
            if (!response.ok && !([400, 422].includes(response.status) && body?.status)) {
              throw new Error(`Bag response ${response.status}`);
            }
            if (!body || (!body.status && !body.id && !Array.isArray(body.items))) {
              throw new Error('Invalid bag response');
            }
            return body;
          })
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              if (await this.refreshPartialAdd(previousCartCount, response.description || response.message, variantId)) {
                return;
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            this.renderCartContents(response);
          })
          .catch((e) => {
            console.error(e);
            const message = window.dongxiNetworkStrings?.addUnconfirmed ||
              'We could not confirm whether this was added. Check your bag before trying again.';
            this.showNetworkError(message, {
              label: window.dongxiNetworkStrings?.checkBag || 'View bag',
              href: routes.cart_url,
            });
            this.dispatchCartErrorEvent(message, 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      clearNetworkError() {
        this.networkErrorWrapper?.remove();
        this.networkErrorWrapper = null;
      }

      showNetworkError(message, action) {
        this.clearNetworkError();
        const wrapper = document.createElement('div');
        wrapper.className = 'product-form__network-error';
        wrapper.setAttribute('role', 'alert');
        const text = document.createElement('p');
        text.textContent = message;
        wrapper.append(text);
        const control = document.createElement(action.href ? 'a' : 'button');
        control.className = 'link underlined-link';
        control.textContent = action.label;
        if (action.href) control.href = action.href;
        else {
          control.type = 'button';
          control.addEventListener('click', action.onClick);
        }
        wrapper.append(control);
        this.form.before(wrapper);
        this.networkErrorWrapper = wrapper;
      }

      validateGiftChoices() {
        let valid = true;
        let firstMissingOption = null;
        for (const choiceType of ['zodiac', 'packaging']) {
          if (this.getAttribute(`data-requires-${choiceType}`) !== 'true') continue;
          const options = Array.from(this.form.elements).filter((input) => input.hasAttribute(`data-${choiceType}-option`));
          const selectedOption = options.find((input) => input.checked);
          if (!selectedOption) {
            valid = false;
            const picker = options[0]?.closest(`dongxi-${choiceType}-picker`);
            if (picker) {
              picker.setAttribute('data-invalid', '');
              picker.querySelector('fieldset').setAttribute('aria-invalid', 'true');
              picker.querySelector('.dx-zodiac__error').hidden = false;
              firstMissingOption ||= options[0];
            }
          } else {
            const codeInput = this.form.elements.namedItem(`properties[_${choiceType}_code]`);
            if (!codeInput) return false;
            codeInput.value = selectedOption.getAttribute(`data-${choiceType}-code`);
          }
        }
        firstMissingOption?.focus();
        return valid;
      }

      getDrawerItemCount() {
        if (this.cart?.localName !== 'cart-drawer') return null;
        return Array.from(this.cart.querySelectorAll('input[name="updates[]"]'))
          .reduce((count, input) => count + (parseInt(input.value, 10) || 0), 0);
      }

      async refreshPartialAdd(previousCount, message, variantId) {
        if (previousCount === null || !message) return false;
        try {
          // A stock-limit response can still add the remaining available items.
          const cartResponse = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
          if (!cartResponse.ok) return false;
          const freshCart = await cartResponse.json();
          if (!Number.isInteger(freshCart.item_count) || freshCart.item_count <= previousCount) return false;

          const sectionsToRender = this.cart.getSectionsToRender();
          const sectionUrl = new URL(routes.cart_url, window.location.origin);
          sectionUrl.searchParams.set('sections', sectionsToRender.map((section) => section.id).join(','));
          const sectionResponse = await fetch(sectionUrl, { cache: 'no-store' });
          if (!sectionResponse.ok) return false;
          const sections = await sectionResponse.json();
          if (sectionsToRender.some((section) => typeof sections[section.id] !== 'string')) return false;

          this.error = false;
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: variantId,
            cartData: freshCart,
          });
          this.renderCartContents({ ...freshCart, sections }, message);
          return true;
        } catch (error) {
          console.error('Unable to refresh the bag after the stock-limit response.', error);
          return false;
        }
      }

      renderCartContents(response, notice = '') {
        const render = () => {
          CartPerformance.measure('add:paint-updated-sections', () => {
            this.cart.classList.remove('is-empty');
            this.cart.renderContents(response);
            if (notice) {
              const message = document.createElement('p');
              message.className = 'cart-drawer__add-notice';
              message.setAttribute('role', 'status');
              message.textContent = notice;
              this.cart.querySelector('.drawer__header').after(message);
            }
          });
        };
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener('modalClosed', () => setTimeout(render), { once: true });
          quickAddModal.hide(true);
        } else {
          render();
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
